import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalBar as BarIcon,
  Refresh as RefreshIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { barService } from '../../services/bar';
import api from '../../services/api';

const BarManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // State
  const [drinks, setDrinks] = useState([]);
  const [events, setEvents] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [summary, setSummary] = useState(null);
  
  // Dialog states
  const [drinkDialog, setDrinkDialog] = useState({ open: false, drink: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, drinkId: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load drinks, events, and config
      const [drinksResult, eventsResult, configResult] = await Promise.all([
        barService.getDrinks(),
        api.get('/api/events/'),
        barService.getBarConfig()
      ]);

      if (drinksResult.success) {
        setDrinks(drinksResult.data);
      }

      if (eventsResult.data) {
        setEvents(eventsResult.data);
      }

      if (configResult.success && configResult.data) {
        setActiveEvent(configResult.data);
        setSelectedEventId(configResult.data.active_event_id || '');
        
        // Load summary if there's an active event
        if (configResult.data.active_event_id) {
          const summaryResult = await barService.getEventSummary(configResult.data.active_event_id);
          if (summaryResult.success) {
            setSummary(summaryResult.data);
          }
        }
      }
    } catch (err) {
      setError('Failed to load bar management data');
      console.error('Bar management loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetActiveEvent = async () => {
    const result = await barService.setActiveEvent(selectedEventId || null);
    
    if (result.success) {
      setSuccess('Active event updated successfully');
      loadData();
    } else {
      setError(result.error);
    }
  };

  const handleOpenDrinkDialog = (drink = null) => {
    setDrinkDialog({ open: true, drink });
  };

  const handleCloseDrinkDialog = () => {
    setDrinkDialog({ open: false, drink: null });
  };

  const handleSaveDrink = async (drinkData) => {
    let result;
    if (drinkDialog.drink) {
      // Update existing drink
      result = await barService.updateDrink(drinkDialog.drink._id, drinkData);
    } else {
      // Create new drink
      result = await barService.createDrink(drinkData);
    }

    if (result.success) {
      setSuccess(result.message);
      handleCloseDrinkDialog();
      loadData();
    } else {
      setError(result.error);
    }
  };

  const handleDeleteDrink = async () => {
    const result = await barService.deleteDrink(deleteDialog.drinkId);
    
    if (result.success) {
      setSuccess('Drink deleted successfully');
      setDeleteDialog({ open: false, drinkId: null });
      loadData();
    } else {
      setError(result.error);
    }
  };

  const getEventName = (eventId) => {
    const event = events.find(e => e._id === eventId);
    return event ? event.name : 'Unknown Event';
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading bar management...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Bar Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadData}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Active Event Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon /> Active Event Configuration
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <FormControl fullWidth>
              <InputLabel>Select Event</InputLabel>
              <Select
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                label="Select Event"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {events.map((event) => (
                  <MenuItem key={event._id} value={event._id}>
                    {event.name} - {new Date(event.date).toLocaleDateString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSetActiveEvent}
              disabled={selectedEventId === (activeEvent?.active_event_id || '')}
            >
              Set Active Event
            </Button>
          </Grid>
        </Grid>
        {activeEvent?.active_event_id && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Current active event: <strong>{getEventName(activeEvent.active_event_id)}</strong>
          </Alert>
        )}
      </Paper>

      {/* Summary Statistics */}
      {summary && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Event Summary - {summary.event_name}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                    {summary.total_revenue.toFixed(2)} CHF
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="secondary.main" sx={{ fontWeight: 'bold' }}>
                    {summary.total_transactions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                    {summary.user_summaries.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Participants
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* User Spending Table */}
          <TableContainer sx={{ mt: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>User</strong></TableCell>
                  <TableCell align="right"><strong>Transactions</strong></TableCell>
                  <TableCell align="right"><strong>Total Spent (CHF)</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.user_summaries.map((userSummary) => (
                  <TableRow key={userSummary.user_id}>
                    <TableCell>{userSummary.username}</TableCell>
                    <TableCell align="right">{userSummary.transaction_count}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {userSummary.total_spent.toFixed(2)} CHF
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Drink Management */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BarIcon /> Drink Menu
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDrinkDialog()}
          >
            Add Drink
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell align="right"><strong>Price (CHF)</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drinks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No drinks available. Add some drinks to get started.
                  </TableCell>
                </TableRow>
              ) : (
                drinks.map((drink) => (
                  <TableRow key={drink._id}>
                    <TableCell>{drink.name}</TableCell>
                    <TableCell>{drink.category}</TableCell>
                    <TableCell align="right">{drink.price.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={drink.available ? 'Available' : 'Unavailable'}
                        color={drink.available ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDrinkDialog(drink)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog({ open: true, drinkId: drink._id })}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Drink Dialog */}
      <DrinkDialog
        open={drinkDialog.open}
        drink={drinkDialog.drink}
        onClose={handleCloseDrinkDialog}
        onSave={handleSaveDrink}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, drinkId: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this drink? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, drinkId: null })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteDrink} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Drink Dialog Component
const DrinkDialog = ({ open, drink, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    category: '',
    available: true
  });

  useEffect(() => {
    if (drink) {
      setFormData({
        name: drink.name,
        price: drink.price,
        category: drink.category,
        available: drink.available
      });
    } else {
      setFormData({
        name: '',
        price: '',
        category: '',
        available: true
      });
    }
  }, [drink, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onSave({
      name: formData.name,
      price: parseFloat(formData.price),
      category: formData.category,
      available: formData.available
    });
  };

  const isValid = formData.name && formData.price > 0 && formData.category;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {drink ? 'Edit Drink' : 'Add New Drink'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Drink Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Price (CHF)"
            type="number"
            value={formData.price}
            onChange={(e) => handleChange('price', e.target.value)}
            fullWidth
            required
            inputProps={{ min: 0, step: 0.5 }}
          />
          <TextField
            label="Category"
            value={formData.category}
            onChange={(e) => handleChange('category', e.target.value)}
            fullWidth
            required
            placeholder="e.g., Bier, Softdrink, Energy Drink"
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.available}
              onChange={(e) => handleChange('available', e.target.value)}
              label="Status"
            >
              <MenuItem value={true}>Available</MenuItem>
              <MenuItem value={false}>Unavailable</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid}
        >
          {drink ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BarManagement;

